import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextareaFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { formatDateDdMmYyyy } from '@/utils/dateUtils';
import { locationModelService } from '../services/locationModelService';

export interface Holiday {
  id: string;
  name: string;
  date: string;
  type: 'national' | 'company' | 'regional';
  isRecurring: boolean;
  description: string;
  officeId: string;
  not_working_percentage: number;
}

interface HolidayDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  holiday: Holiday;
  selectedOfficeId: string;
  existingHolidays: Holiday[];
  onSaved?: () => void;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const HolidayDrawer: React.FC<HolidayDrawerProps> = ({
  isOpen,
  onClose,
  holiday,
  selectedOfficeId,
  existingHolidays,
  onSaved,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const isEdit = !!holiday.id && !readOnly;
  const [formData, setFormData] = useState<Holiday>(holiday);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData(holiday);
      setSubmitError(null);
    }
  }, [isOpen, holiday]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOfficeId) return;

    if (!formData.date) {
      toast.error('Date is required');
      return;
    }

    const holidayName = formData.name.trim() || `Holiday ${formData.date}`;
    const locationId = formData.officeId || selectedOfficeId;

    const duplicateHoliday = existingHolidays.find(
      (h) => h.date === formData.date && h.id !== formData.id
    );

    if (duplicateHoliday) {
      toast.error(
        'A holiday with the same date already exists for this location. Please choose a different date.'
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (formData.id === '') {
        await locationModelService.createLocationModel({
          name: holidayName,
          location_id: locationId,
          date_of_year: formData.date,
          not_working_percentage: formData.not_working_percentage,
        });
      } else {
        await locationModelService.updateLocationModel(formData.id, {
          name: holidayName,
          location_id: locationId,
          date_of_year: formData.date,
          not_working_percentage: formData.not_working_percentage,
        });
      }
      onSaved?.();
      onClose();
    } catch (error) {
      setSubmitError(
        `Failed to save holiday: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const dateDisplay = formData.date ? formatDateDdMmYyyy(formData.date) : '—';
  const notWorkingDisplay = `${(formData.not_working_percentage * 100).toFixed(1)}%`;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View Holiday' : isEdit ? 'Edit Holiday' : 'Add New Holiday'}
      description={
        readOnly
          ? 'Holiday details.'
          : isEdit
            ? 'Update the holiday details below.'
            : 'Fill in the details for the new holiday.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel="Save Holiday"
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextFormField
            label="Holiday Name"
            value={formData.name}
            onChange={(v) => setFormData((prev) => ({ ...prev, name: v }))}
            disabled={readOnly}
            copyable={readOnly}
          />
          <TextFormField
            label="Date"
            type={readOnly ? 'text' : 'date'}
            required={!readOnly}
            value={readOnly ? dateDisplay : formData.date || ''}
            onChange={(v) => setFormData((prev) => ({ ...prev, date: v }))}
            disabled={readOnly}
            copyable={readOnly}
          />
        </div>
        <TextareaFormField
          label="Description"
          value={formData.description}
          onChange={(v) => setFormData((prev) => ({ ...prev, description: v }))}
          disabled={readOnly}
        />
        <TextFormField
          label={readOnly ? 'Not working' : 'Not Working Percentage (0.000 - 1.000)'}
          type={readOnly ? 'text' : 'number'}
          value={readOnly ? notWorkingDisplay : String(formData.not_working_percentage)}
          onChange={(v) => {
            const value = Number.parseFloat(v);
            if (!Number.isNaN(value) && value >= 0 && value <= 1) {
              setFormData((prev) => ({ ...prev, not_working_percentage: value }));
            }
          }}
          disabled={readOnly}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
